import { EMAIL_VARIABLE_LABELS } from 'lib';
import useVariableInsertion from './useVariableInsertion';
import React from 'react';
import {
  Badge,
  Btn,
  Empty,
  Field,
  Grid,
  GroupLabel,
  Inset,
  T,
  Tag,
  Textarea,
  TextInput
} from './ui';

/**
 * Section 3 — the template editor, in the design's two-column layout.
 *
 * Text inputs and textareas, and nothing else: no rich-text control, no HTML view, no image upload,
 * no drag-and-drop. That is the brief's constraint and it is enforced three times over — the
 * endpoint accepts only declared blocks, the values are plain strings, and the renderer escapes
 * them before they reach the email. This component is the first of the three, not the only one.
 */

/*
  The four blocks the endpoint actually accepts, with the design's own field labels.

  Labels and nothing else. Each of these carried a line of guidance underneath — what an eyebrow is
  for, that the title doubles as the subject line, how long a body should be — and the design has no
  such line under any field. They are gone rather than shortened: the fields are a kicker, a title,
  a paragraph and a button label, and a form that explains what a title is reads as though it does
  not expect to be understood.
*/
const FIELDS = {
  eyebrow: { label: 'Eyebrow Text', col: 'left', half: true },
  /*
    The greeting, which used to be read-only and is not any more.

    It was fixed because the renderer wrote it: `buildGreeting` produced "Hi {First Name}," or
    "Hi there,", swapped in as a whole line so a fan with no name never read "Hi ,". That protection
    is still in force — the first-name tag now carries an inline fallback — so the line can be
    written here instead of in code.
  */
  greeting: { label: 'Greeting (supports variables)', col: 'left', half: true, short: true },
  /*
    Two fields where there was one.

    This was a single input labelled "Subject / Title", and the label was the only honest thing
    about it: the value went into the email's `<h1>` and the subject line came from a locale string
    the dashboard never showed. An administrator edited "Subject / Title", watched the preview's
    Subject line change to match, and sent a different subject entirely.
  */
  subject: { label: 'Subject Line', col: 'left', short: true },
  title: { label: 'Title', col: 'left' },
  body: { label: 'Body Text', col: 'left', long: true },
  ctaLabel: { label: 'Primary CTA Text', col: 'right', half: true, short: true },
  /*
    Editable now that there is somewhere for it to go.

    It was read-only with the reason "a destination chosen per campaign has not been built", which
    was true: the campaign email rendered no secondary link at all, so wording typed here would have
    been saved and never sent. The template renders one now and the dispatcher supplies its address,
    so the reason has been answered rather than removed.
  */
  ctaSecondary: { label: 'Secondary CTA Text', col: 'right', half: true, short: true },
  /*
    The compliance footer, per template, layered over the global one.

    It was read-only with a real reason: one line, edited on Settings, identical on all nine, which
    is what keeps approved compliance text from drifting into nine copies. That reason was put to
    the operator and they chose per-template editing knowingly.

    The layering is what is left of the objection. Empty here does not mean "no footer" — unlike
    every other block — it means "use the shared one", so the Settings page still reaches every
    template nobody has overridden, and no email can be sent with no compliance line at all.
  */
  footer: { label: 'Footer Text', col: 'right', long: true }
};

/**
 * Fields where a variable sits in a short line rather than a paragraph.
 *
 * A tag lands *inside* a value, and the row removal that makes a value-less variable safe does not
 * reach inside one. In a paragraph a missing value is a gap; in a five-word button label it is the
 * whole label. So these fields offer only variables that carry a fallback — the same rule the
 * campaign builder already applies to its two inputs, for the same reason.
 */
// No Country: the campaign no longer offers it (see `CAMPAIGN_INSERTABLE_VARIABLES`).
const SHORT_FIELD_VARIABLES = ['First Name', 'Team Name', 'Rank'];

/**
 * Fields the design shows that this build cannot write, and why.
 *
 * Rendered read-only rather than dropped. An operator who has seen the design will look for them,
 * and "it is missing" and "it is decided elsewhere" are very different answers. Each is disabled
 * because the endpoint would reject it — `updateTemplateText` accepts only the template's declared
 * `editableBlocks`, which is what stops this screen from being a way to rewrite a security email.
 *
 * Each `reason` used to print as a paragraph under its field, which is three paragraphs of prose in
 * a form the design draws as eight plain inputs. They are the control's `title` now: on the field
 * itself, where someone who finds it greyed out and wonders why will hover, and out of the way of
 * everyone who does not.
 */
const READ_ONLY = {
  templateName: {
    label: 'Template Name',
    col: 'left',
    fixed: true,
    reason:
      'Fixed. Every automatic email finds its template by this name, so changing it would stop them arriving.',
    value: (template) => template?.key || ''
  },
};

/** Field order within each column, as the design lays them out. */
const COL_ORDER = {
  left: ['templateName', 'eyebrow', 'greeting', 'subject', 'title', 'body'],
  right: ['ctaLabel', 'ctaSecondary', 'footer']
};

/**
 * Templates a fan receives at a security or billing moment, from the template's own delivery class.
 *
 * Said before an administrator types, not after they save. Every template's copy is editable now,
 * which includes the email telling someone their password was reset — and the difference between
 * that and a marketing send is not visible from a form full of identical inputs.
 */
const SENSITIVE_CATEGORIES = ['security', 'billing'];

const TemplateTextEditor = ({
  template,
  blocks,
  editableBlocks = [],
  onChange,
  onSave,
  onPreview,
  onShowVersions,
  footerText,
  busy,
  saving,
  dirty
}) => {
  /*
    Caret tracking and insertion, shared with the Campaign Builder.

    Extracted rather than duplicated: the part worth protecting is caret restoration after an
    insertion, and a second copy of it diverges silently — the symptom being an operator's second
    variable overwriting their first.
  */
  const { refFor, rememberSelection, insertVariable } = useVariableInsertion({
    fields: blocks,
    onChange,
    names: editableBlocks
  });

  const fixedCopy = editableBlocks.length === 0;

  /*
    Which field the caret is in, so the chip row can offer what is safe to insert there.

    A greeting or a button label is a short line with no row to remove, so it takes only variables
    carrying a fallback. A paragraph is more forgiving — a missing word there leaves a sentence that
    still reads — but not unrestricted: the note that used to stand here said the body "may take any
    variable" because a missing value is inside a removable block, and that was never true of block
    text. Removing the row would delete the administrator's own sentence, so the renderer substitutes
    nothing instead. A tag with no value leaves a hole either way; the short fields only make the
    hole bigger.
  */
  const [focusedField, setFocusedField] = React.useState(null);

  /*
    Only the variables this template's trigger actually supplies.

    Every template declares them — `welcome` supplies `firstName` and nothing else, `billingNotice`
    supplies the subscription fields — and the endpoint has always sent the list. The chip row
    ignored it and offered all fifteen, so a welcome email could be given `{Price}`, `{Card Type}`
    and `{Last4Digits}`, none of which a welcome send has any value for. Each resolved to nothing
    and the operator was left looking at copy that had silently lost words.

    Falls back to the full list when the field is absent rather than rendering an empty row, so an
    older API response degrades to the previous behaviour instead of to a panel with no chips in it.
  */
  const supported = template?.variableLabels?.length
    ? EMAIL_VARIABLE_LABELS.filter((label) => template.variableLabels.includes(label))
    : EMAIL_VARIABLE_LABELS;

  const offeredVariables =
    focusedField && FIELDS[focusedField]?.short
      ? supported.filter((label) => SHORT_FIELD_VARIABLES.includes(label))
      : supported;

  /*
    Whether this template reaches a fan at a security or billing moment.

    From the template's own category rather than a list of keys here, so a template added later is
    classified by what it is instead of by whether someone remembered to add it.
  */
  const sensitive = SENSITIVE_CATEGORIES.includes(template?.category);

  const editableField = (block) => {
    const field = FIELDS[block];
    if (!field) return null;

    const shared = {
      value: blocks[block] || '',
      onSelect: rememberSelection(block),
      onClick: rememberSelection(block),
      onKeyUp: rememberSelection(block),
      onFocus: () => setFocusedField(block),
      onChange: (event) => onChange({ ...blocks, [block]: event.target.value })
    };

    /*
      An empty footer is not an empty email footer.

      Every other field left blank removes its element. This one falls back to the shared compliance
      line, so the placeholder shows what will actually send rather than leaving the operator to
      assume their email is going out without one.
    */
    const placeholder =
      block === 'footer'
        ? footerText || 'Set under Compliance & Global Footer Configuration on the Settings page.'
        : undefined;

    return (
      <Field key={block} label={field.label}>
        {field.long ? (
          <Textarea
            {...shared}
            placeholder={placeholder}
            rows={6}
            minHeight="150px"
            ref={refFor(block)}
          />
        ) : (
          <TextInput {...shared} placeholder={placeholder} ref={refFor(block)} />
        )}
      </Field>
    );
  };

  const readOnlyField = (key) => {
    const field = READ_ONLY[key];

    /*
      No badge beside any of these labels, which is a real loss and taken deliberately.

      Three of the four are decisions rather than gaps — the name identifies the template, the
      greeting is generated per recipient, the footer is global — and a "Read only" chip on each put
      a marker beside four of this form's eight labels, where the design draws none. Secondary CTA
      *is* a genuine gap, and its "Not available yet" badge was the one signal here worth keeping;
      it went with the others because it is the loudest thing on the card and it is not in the
      design either.

      What survives is the `title` below: every one of these fields answers "why can I not type in
      this" on hover, and `index.test.js` still records that the per-campaign destination is unbuilt.
    */
    return (
      <Field key={key} label={field.label}>
        {field.long ? (
          <Textarea
            readOnly
            disabled
            rows={3}
            minHeight="78px"
            title={field.reason}
            value={field.value(template, footerText)}
          />
        ) : (
          <TextInput
            readOnly
            disabled
            title={field.reason}
            value={field.value(template, footerText)}
          />
        )}
      </Field>
    );
  };

  const column = (side) =>
    COL_ORDER[side].filter((key) => READ_ONLY[key] || editableBlocks.includes(key));

  const render = (key) => (FIELDS[key] ? editableField(key) : readOnlyField(key));

  /**
   * Renders a column, pairing consecutive half-width fields onto one row.
   *
   * The design puts Eyebrow Text beside the Greeting, and Primary CTA beside Secondary CTA. Pairing
   * by a flag rather than by hard-coding the pairs means a field that becomes editable later joins
   * the layout instead of needing it rewritten.
   */
  const renderColumn = (side) => {
    const keys = column(side);
    const out = [];

    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i];
      const isHalf = (FIELDS[key] || READ_ONLY[key])?.half;
      const nextKey = keys[i + 1];
      const nextIsHalf = nextKey && (FIELDS[nextKey] || READ_ONLY[nextKey])?.half;

      if (isHalf && nextIsHalf) {
        out.push(
          <Grid key={`${key}-${nextKey}`} cols="1fr 1fr" gap="12px">
            {render(key)}
            {render(nextKey)}
          </Grid>
        );
        i += 1;
      } else {
        out.push(render(key));
      }
    }

    return out;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {fixedCopy ? (
        <Empty title="This template's wording is fixed">
          Its copy is client-approved and changes through code review, not from here. Only the
          campaign template has editable text - the fields below show what this one sends.
        </Empty>
      ) : null}

      {/*
        Said before the typing, not after the save.

        Eight of these nine templates used to be uneditable, and that rule was the thing standing
        between this form and a reworded password-reset email. It has been lifted deliberately. What
        replaces it is attribution and restore — and this line, because a form of identical inputs
        gives an operator no way to tell which email they are about to change.
      */}
      {sensitive && !fixedCopy ? (
        <Inset>
          <GroupLabel style={{ color: T.amber }}>
            This email is sent at a security or billing moment
          </GroupLabel>
          <span style={{ fontSize: 12.5, color: T.muted }}>
            A fan receives it when something has happened to their account, not as marketing. Its
            wording is what tells them what to do. Every save here is recorded against your name and
            the previous version stays restorable.
          </span>
        </Inset>
      ) : null}

      <Grid cols="1fr 1fr" gap="24px">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
          {renderColumn('left')}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
          {renderColumn('right')}

          {/*
            The personalization tags.

            Three paragraphs stood under these chips — what happens to a variable with no value on
            record, that {Team} and {Location} are accepted short spellings, and what {Country}
            falls back to. All three were true and none is in the design, which shows the chips and
            nothing else. They were notes about a fallback an operator never sees working, which is
            the definition of something the interface does not need to say.

            Rendered as the design writes them, `{First Name}` rather than `+ First Name`: the chip
            now shows the token it inserts. One chip per variable, canonical spelling only — an
            alias is accepted on input but never offered, so nobody is shown the same variable twice
            under two names.
          */}
          <Inset tint>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'wrap'
              }}
            >
              <GroupLabel style={{ color: T.primary }}>Dynamic personalization tags</GroupLabel>
              {/*
                The design's word here is "Click to copy". These insert at the caret instead, which
                is the better of the two and already built — so the label says what the chips do.
              */}
              <span style={{ fontSize: 12, fontWeight: 600, color: T.primary }}>
                Click to insert
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {offeredVariables.map((label) => (
                <Tag
                  key={label}
                  type="button"
                  disabled={fixedCopy || busy}
                  onClick={() => insertVariable(label)}
                >
                  {`{${label}}`}
                </Tag>
              ))}
            </div>
          </Inset>

          {/*
            The design pairs "Save Template" with "Save as New Version". Those would be the same
            request: `updateTemplateText` snapshots the previous copy on every save, which is what
            makes restore possible. Two buttons for one action invites an operator to believe one of
            them does not version — so the second offers the history those saves produce instead.
          */}
          <Grid cols="1fr 1fr" gap="10px">
            <Btn onClick={onSave} disabled={fixedCopy || busy || !dirty}>
              {saving ? 'Saving…' : 'Save Template'}
            </Btn>
            <Btn variant="outline" icon="File" onClick={onShowVersions}>
              Version History
            </Btn>
            <Btn variant="outline" icon="Monitor" onClick={() => onPreview('desktop')}>
              Preview Desktop
            </Btn>
            <Btn variant="outline" icon="Phone" onClick={() => onPreview('mobile')}>
              Preview Mobile
            </Btn>
          </Grid>
        </div>
      </Grid>
    </div>
  );
};

/**
 * The editor's two states, named for what they are.
 *
 * The clean state read "Draft Mode", which is the design's word and is the wrong one here: it
 * labelled the case where there is *nothing* unsaved, beside a badge that says "Unsaved changes"
 * for the case where there is. An operator reading "Draft Mode" reasonably concluded their edits
 * were pending — and could leave the page believing work was held that had in fact been saved, or
 * not made.
 *
 * There is no draft to be in, either: `updateTemplateText` writes a new version immediately, and
 * every version is live. So the clean state says the template is saved, and names the version.
 */
export const DraftBadge = ({ dirty, version }) => (
  <Badge tone={dirty ? 'amber' : 'indigo'}>
    {dirty ? 'Unsaved changes' : version ? `Saved — version ${version}` : 'Saved'}
  </Badge>
);

export default TemplateTextEditor;
